// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import Truncate from "@bg/App/Components/Truncate";
import LazyImage, { LazyImageProps } from "@bg/MobiusOverrides/LazyImage";
import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import translate from "@insite/client-framework/Translate";
import { AutocompleteItemModel, ProductAutocompleteItemModel } from "@insite/client-framework/Types/ApiModels";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React from "react";
import { css } from "styled-components";

interface Props {
    products: ProductAutocompleteItemModel[];
    viewAll: any;
    focusedItem?: AutocompleteItemModel;
    goToUrl: (url: string) => void;
    extendedStyles?: AutocompleteProductsStyles;
}

export interface AutocompleteProductsStyles {
    headerText?: TypographyPresentationProps;
    headerWrapper?: InjectableCss;
    viewAllText?: InjectableCss;
    container?: GridContainerProps;
    focusedContainer?: GridContainerProps;
    imageGridItem?: GridItemProps;
    image?: LazyImageProps;
    infoGridItem?: GridItemProps;
    erpGridItem?: GridItemProps;
    titleLink?: LinkPresentationProps;
    erpNumberText?: TypographyPresentationProps;
    productGridItem?: GridItemProps;
}

export const autocompleteProductsStyles: AutocompleteProductsStyles = {
    headerText: {
        size: 13,
        weight: 500,
        lineHeight: "22px",
        color: "text.main",
        css: css`
            letter-spacing: 0.2px;
            text-align: left;
            text-transform: uppercase;
        `,
    },
    headerWrapper: {
        css: css`
            display: flex;
            justify-content: space-between;
            width: 100%;
            margin-bottom: 15px;
        `,
    },
    viewAllText: {
        css: css`
            font-size: 15px;
            letter-spacing: 0.2px;
            line-height: 16px;
            color: ${getColor("secondary")};
            text-transform: capitalize;

            &:hover,
            &:focus {
                text-decoration: underline;
            }
        `,
    },
    productGridItem: {
        width: [12, 6, 4, 3, 2.4] as any,
    },
    imageGridItem: {
        width: 12,
        css: css`
            > div {
                width: 100%;
                height: 155px;
                display: flex;
                justify-content: center;
                align-items: center;
                margin: auto;
                cursor: pointer;
                img {
                    max-width: 155px;
                    max-height: 155px;
                }
            }
        `,
    },
    image: {},
    infoGridItem: {
        width: 12,
        css: css`
            height: 66px;
            margin-bottom: -5px;
        `,
    },
    titleLink: {
        typographyProps: {
            css: css`
                &,
                * {
                    letter-spacing: 0px;
                    text-align: left;
                    color: ${getColor("secondary")};
                    font-size: 15px;
                    font-weight: 500;
                }
            `,
        },
    },
    erpGridItem: {
        width: 12,
    },
    erpNumberText: {},
};

const styles = autocompleteProductsStyles;

class AutocompleteProducts extends React.Component<Props> {
    private readonly styles: AutocompleteProductsStyles;

    constructor(props: Props) {
        super(props);

        this.styles = mergeToNew(styles, props.extendedStyles);
    }

    render() {
        const { products, viewAll } = this.props;
        const styles = this.styles;
        return (
            <>
                <StyledWrapper {...styles.headerWrapper}>
                    <Typography
                        {...styles.headerText}
                        data-test-selector="autocompleteProducts_Header"
                        className="autocomplete-products-header"
                    >
                        {translate("Product Matches")}
                    </Typography>
                    <Link
                        aria-label={translate("view all results")}
                        {...styles.viewAllText}
                        onClick={viewAll}
                        color="primary"
                    >
                        {translate("view all products result")}
                    </Link>
                </StyledWrapper>
                <GridContainer gap={30}>
                    {products.map(product => (
                        <GridItem
                            key={product.id!}
                            onClick={() => {
                                this.props.goToUrl(product.url);
                            }}
                            {...styles.productGridItem}
                            className="autocomplete-products-container"
                        >
                            <StyledWrapper
                                css={css`
                                    width: 100%;
                                    height: 100%;
                                `}
                            >
                                <GridContainer gap={10}>
                                    <GridItem
                                        {...styles.imageGridItem}
                                        className="autocomplete-products-image-grid-item"
                                    >
                                        <LazyImage
                                            {...styles.image}
                                            height="155"
                                            width="155"
                                            cloudinaryWidth="155"
                                            altText={product.title || product.name}
                                            src={product.image}
                                            className="autocomplete-products-image"
                                        />
                                    </GridItem>
                                    <GridItem {...styles.infoGridItem} className="autocomplete-products-info-grid-item">
                                        <Link
                                            {...styles.titleLink}
                                            data-test-selector={`productId_${product.id}`}
                                            className="autocomplete-products-link"
                                        >
                                            <Truncate lines={3}>{product.title}</Truncate>
                                        </Link>
                                    </GridItem>
                                    <GridItem {...styles.erpGridItem}>
                                        <Typography
                                            size={13}
                                            lineHeight="20px"
                                            css={css`
                                                letter-spacing: 0;
                                                font-weight: 500;
                                                white-space: nowrap;
                                                display: flex;
                                                max-width: 100%;
                                            `}
                                            color="text.main"
                                            data-test-selector="autocompleteProducts_PartNumber"
                                            className="autocomplete-products-erp-number"
                                        >
                                            {translate("MFR Part #:")}&nbsp;
                                            <Typography
                                                css={css`
                                                    font-weight: 300;
                                                `}
                                                ellipsis={true}
                                            >
                                                {product.manufacturerItemNumber}
                                            </Typography>
                                        </Typography>
                                    </GridItem>
                                </GridContainer>
                            </StyledWrapper>
                        </GridItem>
                    ))}
                </GridContainer>
            </>
        );
    }
}

export default AutocompleteProducts;
